import { z } from 'zod';
import { DOCUMENT_TYPES } from '../types/documentTemplates.types';

export const MAX_BODY_BYTES = 102400;

const utf8Length = (value: string): number => new TextEncoder().encode(value).length;

const nameField = z.string().trim().min(1, 'Name is required').max(150, 'Name must be 150 characters or fewer');
const descriptionField = z.string().max(2000, 'Description must be 2000 characters or fewer');
const documentTypeField = z.enum(DOCUMENT_TYPES, { errorMap: () => ({ message: 'Select a document type' }) });
const changeNoteField = z.string().max(500, 'Change note must be 500 characters or fewer');
const bodyField = z
  .string()
  .min(1, 'Body is required')
  .refine((value) => utf8Length(value) <= MAX_BODY_BYTES, {
    message: `Body must be ${MAX_BODY_BYTES / 1024} KB or smaller`,
  });

export const templateMetadataSchema = z.object({
  name: nameField,
  description: descriptionField,
  documentType: documentTypeField,
});

export const draftSchema = z.object({
  bodyHtml: bodyField,
  changeNote: changeNoteField,
});

const keyField = z
  .string()
  .regex(/^[a-z][a-z0-9_]{2,63}$/, 'Use 3-64 lowercase letters, digits or underscores, starting with a letter');

export const createTemplateSchema = templateMetadataSchema
  .extend({
    key: keyField,
    ownerKind: z.enum(['platform', 'dealer']),
    ownerDealerId: z.string().nullable(),
  })
  .refine((values) => values.ownerKind === 'platform' || !!values.ownerDealerId, {
    message: 'Select a dealer',
    path: ['ownerDealerId'],
  });

export const customiseTemplateSchema = z.object({
  name: nameField,
  key: keyField,
  dealerId: z.string().min(1, 'Select a dealer'),
});

export type TemplateMetadataFormValues = z.infer<typeof templateMetadataSchema>;
export type DraftFormValues = z.infer<typeof draftSchema>;
export type CreateTemplateFormValues = z.infer<typeof createTemplateSchema>;
export type CustomiseTemplateFormValues = z.infer<typeof customiseTemplateSchema>;
